import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { omit } from 'remeda';
import apiService from 'shared/service/api.service';
import { ApplicationLocaleSchema } from 'types';
import { z } from 'zod/v4';

import { type ApiError, addResourceNameToQueryResult, useAxiosInstance } from './utils';

const TenantSchema = z
  .object({
    id: z.number(),
    subdomain: z.string().min(1),
    companyName: z.string().min(1),
    groupId: z.number().nullable(),
    logo: z.url().nullable(),
  })
  .transform(data => ({
    ...data,
    url: window.location.origin.replace(apiService.tenant, data.subdomain),
  }));

const ProfileSchema = z
  .object({
    id: z.number(),
    profilePicture: z.url().nullable(),
    accessibleTenants: z.array(TenantSchema),
    group: z
      .object({ id: z.number(), name: z.string().min(1), companiesCount: z.number() })
      .nullable(),
    email: z.email(),
    phone: z.string().nullable(),
    firstName: z.string().min(1),
    hasAcceptedGc: z.boolean(),
    intercomHash: z.string(),
    isActive: z.boolean(),
    lastName: z.string().min(1),
    locale: ApplicationLocaleSchema,
    preferences: z.object({
      itemsPerPage: z.number(),
      emails: z.object({
        daily: z.boolean(),
        weekly: z.boolean(),
        notifications: z.boolean(),
        tasks: z.boolean(),
      }),
    }),
    roles: z.array(z.string()),
    signature: z.string().nullable(),
  })
  .transform(data => ({
    ...omit(data, ['firstName', 'lastName']),
    isAdmin: data.roles.includes('admin') || data.roles.includes('superadmin'),
    isSuperAdmin: data.roles.includes('superadmin'),
    permissions: {
      canManageRecoveryPlans: data.roles.includes('workflow'),
    },
    name: {
      first: data.firstName,
      last: data.lastName,
      full: `${data.firstName} ${data.lastName}`,
      initials: `${data.firstName.charAt(0).toUpperCase()}${data.lastName.charAt(0).toUpperCase()}`,
    },
  }));

export type Profile = z.infer<typeof ProfileSchema>;

export async function loadProfileQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;

  const { data } = await instance.get('/profile');
  return ProfileSchema.parse(data);
}

interface LoadProfileVariables {
  enabled?: boolean;
}

export const useLoadProfile = ({ enabled = true }: LoadProfileVariables) => {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<Profile, ApiError>({
    enabled,
    queryKey: ['profile'],
    queryFn: () => loadProfileQueryFn(axiosInstance),
  });

  return addResourceNameToQueryResult<Profile, unknown, 'profile'>('profile', queryResult);
};
